import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import TaskBreakdownForm from "./TaskForm";

import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button_Names } from "@/constants/global-constants";
import { TASK_CLAIMS_CONSTANTS } from "@/constants/task-claims.constant";
import useQueryParams from "@/hooks/useQueryParams";
import { TaskBreakdownSheetProps } from "@/pages/task-and-claims/types";
import {
  useGetSingleApprovedQuery,
  useUpdateTaskBreakDownMutation,
} from "@/redux/api/claimsQuery";
import {
  ITaskBreakDownData,
  ITaskBreakDownSchema,
  TaskBreakdownSchema,
} from "@/schemas/task-and-claims";

// const validateBreakDowns = (
//   taskBreakDown: ITaskBreakDownData[],
//   claimBreakDown: IClaimBreakDownData[],
// ): {
//   parentIndex: number;
//   childIndex: number;
//   message: string;
// } | null => {
//   for (let i = 0; i < taskBreakDown.length; i++) {
//     const task = taskBreakDown[i];
//     const claim = claimBreakDown.find(
//       (cb) => cb.name === task.name && cb.position === task.position,
//     );

//     if (claim) {
//       for (let j = 0; j < task.details.length; j++) {
//         const taskDetail = task.details[j];
//         const claimDetail = claim.details.find(
//           (cd) => cd.item === taskDetail.item,
//         );

//         if (claimDetail && taskDetail.newProgress < claimDetail.claimed) {
//           return {
//             parentIndex: i,
//             childIndex: j,
//             message: `${TASK_CLAIMS_CONSTANTS.MESSAGES.NEW_BIG_PAST_CLAIM}: ${claimDetail.claimed}`,
//           };
//         }
//       }
//     }
//   }

//   return null;
// };

const TaskBreakdownSheet = ({
  sheet,
  setSheet,
  customAction,
}: TaskBreakdownSheetProps): React.JSX.Element => {
  const { keys } = useQueryParams();
  const rowId = keys.rowId as string;

  const { data } = useGetSingleApprovedQuery(
    {
      claimsId: rowId,
      claimDetailId: sheet.itemId,
    },
    { skip: !sheet.itemId || !rowId },
  );

  const taskBreakDownData = useMemo(() => {
    if (customAction) {
      return customAction.defaultValues;
    }

    return data?.taskBreakDown;
  }, [rowId, data, customAction]) as ITaskBreakDownData[];

  // const claimBreakDownData = useMemo(() => {
  //   if (customAction) {
  //     return customAction.claimBreakDown;
  //   }

  //   return data?.claimBreakDown;
  // }, [rowId, data, customAction]) as IClaimBreakDownData[];

  const initialTotal = customAction
    ? customAction.defaultValues.reduce((acc, curr) => acc + curr.total, 0)
    : taskBreakDownData?.reduce((acc, curr) => acc + curr.total, 0);

  const form = useForm<ITaskBreakDownSchema>({
    resolver: zodResolver(TaskBreakdownSchema),
    values: {
      data: customAction ? customAction.defaultValues : taskBreakDownData || [],
    },
  });

  const [updateTaskBreakDown] = useUpdateTaskBreakDownMutation();

  const handleExit = (): void => {
    setSheet({ itemId: "", isOpen: !sheet.isOpen });
    form.reset();
  };

  const onSubmit = async (data: ITaskBreakDownSchema): Promise<void> => {
    //Check if the items claimBreakDown and taskBreakDown are new progress is less than claimed value
    // const error = validateBreakDowns(data.data, claimBreakDownData);
    // if (error) {
    //   form.setError(
    //     `data.${error.parentIndex}.details.${error.childIndex}.newProgress`,
    //     {
    //       message: error.message,
    //     },
    //   );

    //   return;
    // }

    //Currently CustomAction is used on Daily Report Page -> Claims Details
    if (customAction) {
      customAction.onSave(data.data);
      handleExit();

      return;
    }

    const res = await updateTaskBreakDown({
      claimsId: rowId,
      claimsDetailId: sheet.itemId,
      data: data?.data,
    });

    if (res.data) {
      toast.success(TASK_CLAIMS_CONSTANTS.MESSAGES.TASK_BD_UPDATED);
      handleExit();
    }
  };

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  return (
    <div>
      <Sheet open={sheet?.isOpen} onOpenChange={handleExit}>
        <SheetContent className='min-w-[80dvw] overflow-y-auto min-h-full'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <SheetHeader>
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-2 '>
                    <Button
                      variant={"outline"}
                      size={"icon"}
                      onClick={handleExit}
                    >
                      <ChevronLeft className='text-primary' />
                    </Button>
                    <SheetTitle className='text-xl font-medium capitalize'>
                      Task Breakdown
                    </SheetTitle>
                  </div>
                  <div className='flex justify-between gap-2'>
                    <Button
                      variant='ghost'
                      disabled={!isDirty}
                      onClick={() => form.reset()}
                    >
                      {(initialTotal || 0) > 0
                        ? Button_Names.Discard_Change
                        : Button_Names.Reset}
                    </Button>
                    <Button type='submit' disabled={!isDirty}>
                      Save Progress
                    </Button>
                  </div>
                </div>
              </SheetHeader>
              <main className='my-4'>
                <TaskBreakdownForm isHideItem={customAction?.hideItem} />
              </main>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default TaskBreakdownSheet;
